import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/design-system";

import type { CompanyUsageRow } from "../api/usage.service";
import { formatarBytes } from "./formatarBytes";

interface UsageRankingProps {
  rows: CompanyUsageRow[];
  isLoading: boolean;
  onSelectCompany?: (companyId: string) => void;
}

const nf = new Intl.NumberFormat("pt-BR");
const VISIVEIS_INICIAL = 12;

function Esqueleto() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-4 w-44 animate-pulse rounded bg-gray-200" />
          <div className="h-5 flex-1 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

/**
 * Ranking de empresas por volume, uma linha cada.
 *
 * **Por que linha e não cartão.** Com duzentos clientes, um cartão por empresa
 * repete dez rótulos duzentas vezes para mostrar números que o resumo do topo
 * já agregou. Em linha, cada empresa ocupa uma altura de texto e a comparação —
 * que é o que se procura ao varrer a lista — sai da barra, sem precisar ler.
 *
 * Os números detalhados aparecem no drill-down: escolhendo a empresa, a tela
 * inteira se refaz para ela.
 *
 * Começa com doze e expande sob demanda. Duzentas linhas de uma vez empurram os
 * gráficos para fora da tela, e quem abre a página quer ver o topo do ranking.
 */
export function UsageRanking({
  rows,
  isLoading,
  onSelectCompany,
}: UsageRankingProps) {
  const [expandido, setExpandido] = useState(false);

  if (isLoading) return <Esqueleto />;

  const comVolume = rows.filter((r) => r.messagesTotal > 0);
  if (comVolume.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-500">
        Nenhuma atividade registrada no período selecionado.
      </div>
    );
  }

  const maior = comVolume[0].messagesTotal || 1;
  const mostradas = expandido
    ? comVolume
    : comVolume.slice(0, VISIVEIS_INICIAL);
  const restantes = comVolume.length - mostradas.length;

  return (
    <div>
      <div className="space-y-1">
        {mostradas.map((r) => (
          <div
            key={r.companyId}
            onClick={
              onSelectCompany ? () => onSelectCompany(r.companyId) : undefined
            }
            className={`flex items-center gap-3 rounded-md px-2 py-1.5 ${
              onSelectCompany ? "cursor-pointer hover:bg-violet-50/60" : ""
            }`}
          >
            <span
              className="w-44 shrink-0 truncate text-sm text-gray-700"
              title={r.companyName ?? undefined}
            >
              {r.companyName ?? <span className="text-gray-400">sem nome</span>}
            </span>

            <div className="h-5 flex-1 overflow-hidden rounded bg-gray-100">
              <div
                className="h-full rounded bg-violet-600"
                style={{
                  width: `${Math.max(1, (r.messagesTotal / maior) * 100)}%`,
                }}
              />
            </div>

            <span className="w-24 shrink-0 text-right text-sm tabular-nums text-gray-900">
              {nf.format(r.messagesTotal)}
            </span>
            <span className="hidden w-20 shrink-0 text-right text-xs tabular-nums text-gray-500 sm:block">
              {formatarBytes(r.storageBytes)}
            </span>
            <span className="w-14 shrink-0 text-right text-xs tabular-nums text-gray-500">
              {r.shareOfMessages.toFixed(1).replace(".", ",")}%
            </span>
          </div>
        ))}
      </div>

      {restantes > 0 && (
        <Button variant="outline" size="sm"
          type="button"
          onClick={() => setExpandido(true)}
          className="mt-3 w-full text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        >
          <ChevronDown size={15} />
          Ver mais {restantes} {restantes === 1 ? "empresa" : "empresas"}
        </Button>
      )}
    </div>
  );
}
